import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import {
  buildStarterPrompts,
  buildWorkflowCards,
  describeFileKind,
  getPendingAssistantReplyCount,
} from "./chat-ux";
import type {
  ChatItem,
  ChatMessage,
  ClassHomeItem,
  ClassItem,
  FileItem,
  JobItem,
  MemberItem,
  RecentChatItem,
  User,
} from "./types";
import {
  canManageClassroomControls,
  deriveWorkspaceSurface,
  mergeHomeClasses,
} from "./workspace-ux";

type LiveEvent =
  | { type: "user-message"; chatId: string; payload: ChatMessage }
  | { type: "assistant-message"; chatId: string; payload: ChatMessage };

const emptyRegister = {
  email: "",
  displayName: "",
  password: "",
  role: "TEACHER" as User["role"],
};

const emptyLogin = {
  email: "",
  password: "",
};

function formatTimeLabel(value: string): string {
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateTimeLabel(value: string): string {
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildChatPreview(chatId: string, messages: ChatMessage[]): string {
  const latest = [...messages]
    .reverse()
    .find((message) => message.chat_id === chatId);
  if (!latest) return "No messages yet";

  if (latest.image_url && !latest.content.trim()) {
    return "Generated image";
  }

  const content = latest.content.replace(/\s+/g, " ").trim();
  return content.length > 58 ? `${content.slice(0, 58)}…` : content;
}

function isFallbackAssistantMessage(message: ChatMessage): boolean {
  return (
    message.role === "ASSISTANT" &&
    message.content.startsWith("RoboLabX fallback reply.")
  );
}

function workspaceLabelForRole(role: User["role"]): string {
  if (role === "ADMIN") return "Admin workspace";
  if (role === "STUDENT") return "Student workspace";
  return "Teacher workspace";
}

function chatCountLabel(count: number): string {
  return `${count} ${count === 1 ? "chat" : "chats"}`;
}

function fileKindLabel(file: FileItem): string {
  if (describeFileKind(file.content_type) === "image") return "Image";
  if (file.content_type.includes("pdf")) return "PDF";
  if (file.content_type.includes("presentation")) return "Slides";
  if (file.content_type.includes("sheet") || file.content_type.includes("csv")) {
    return "Sheet";
  }
  if (file.content_type.startsWith("text/")) return "Text";
  return "Material";
}

export function App() {
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [members, setMembers] = useState<MemberItem[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [chats, setChats] = useState<ChatItem[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [homeClasses, setHomeClasses] = useState<ClassHomeItem[]>([]);
  const [recentChats, setRecentChats] = useState<RecentChatItem[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [registerForm, setRegisterForm] = useState(emptyRegister);
  const [loginForm, setLoginForm] = useState(emptyLogin);
  const [authView, setAuthView] = useState<"login" | "register">("login");
  const [allowAdminBootstrap, setAllowAdminBootstrap] = useState(false);
  const [bootstrapMode, setBootstrapMode] = useState<
    "open" | "restricted" | "closed"
  >("closed");
  const [classForm, setClassForm] = useState({
    name: "",
    subject: "",
    gradeBand: "Years 5-6",
  });
  const [memberForm, setMemberForm] = useState({
    email: "",
    role: "STUDENT" as "TEACHER" | "STUDENT",
  });
  const [chatTitle, setChatTitle] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [liveStatus, setLiveStatus] = useState<
    "connecting" | "live" | "offline"
  >("offline");
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const homeClassSummaries = useMemo(
    () => mergeHomeClasses(classes, homeClasses),
    [classes, homeClasses],
  );

  const selectedClass = useMemo(
    () =>
      homeClassSummaries.find((item) => item.id === selectedClassId) ??
      classes.find((item) => item.id === selectedClassId) ??
      null,
    [classes, homeClassSummaries, selectedClassId],
  );

  const selectedChat = useMemo(
    () => chats.find((item) => item.id === selectedChatId) ?? null,
    [chats, selectedChatId],
  );

  const surface = useMemo(
    () => deriveWorkspaceSurface(selectedClassId),
    [selectedClassId],
  );

  const canManageControls = useMemo(
    () => (user ? canManageClassroomControls(user.role) : false),
    [user],
  );

  const readyFileCount = useMemo(
    () => files.filter((file) => file.status === "READY").length,
    [files],
  );

  const readyReferenceFiles = useMemo(
    () =>
      [...files]
        .filter((file) => file.status === "READY")
        .sort((left, right) => right.created_at.localeCompare(left.created_at))
        .slice(0, 3),
    [files],
  );

  const pendingFileCount = useMemo(
    () => files.filter((file) => file.status !== "READY").length,
    [files],
  );

  const imageFileCount = useMemo(
    () =>
      files.filter((file) => describeFileKind(file.content_type) === "image")
        .length,
    [files],
  );

  const activeJobCount = useMemo(
    () =>
      jobs.filter((job) => job.status === "queued" || job.status === "running")
        .length,
    [jobs],
  );

  const selectedChatPendingReply = useMemo(
    () =>
      jobs.some(
        (job) =>
          job.chat_id === selectedChatId &&
          job.type === "assistant-reply" &&
          (job.status === "queued" || job.status === "running"),
      ),
    [jobs, selectedChatId],
  );

  const activeHomeClasses = useMemo(
    () => homeClassSummaries.filter((item) => item.chat_count > 0),
    [homeClassSummaries],
  );

  const idleHomeClasses = useMemo(
    () => homeClassSummaries.filter((item) => item.chat_count === 0),
    [homeClassSummaries],
  );

  const starterPrompts = useMemo(
    () => buildStarterPrompts(selectedClass, readyReferenceFiles),
    [selectedClass, readyReferenceFiles],
  );

  const pendingAssistantReplyCount = useMemo(
    () => getPendingAssistantReplyCount(jobs, selectedChat?.id ?? null),
    [jobs, selectedChat],
  );

  const workflowCards = useMemo(
    () =>
      user
        ? buildWorkflowCards({
            role: user.role,
            className: selectedClass?.name ?? null,
            selectedChatTitle: selectedChat?.title ?? null,
            liveStatus,
            readyFileCount,
            pendingFileCount,
            imageFileCount,
            selectedChatPendingReply,
          })
        : [],
    [
      imageFileCount,
      liveStatus,
      pendingFileCount,
      readyFileCount,
      selectedChat?.title,
      selectedChatPendingReply,
      selectedClass?.name,
      user,
    ],
  );

  const showJobsPanel = useMemo(
    () =>
      jobs.some(
        (job) =>
          job.status === "queued" ||
          job.status === "running" ||
          job.status === "failed",
      ),
    [jobs],
  );

  async function loadClassWorkspaceData(
    classId: string,
    currentChatId: string | null,
  ) {
    const [membersResponse, filesResponse, chatsResponse, jobsResponse] =
      await Promise.all([
        api.listMembers(classId),
        api.listFiles(classId),
        api.listChats(classId),
        api.listJobs(classId),
      ]);

    setMembers(membersResponse.members);
    setFiles(filesResponse.files);
    setChats(chatsResponse.chats);
    setJobs(jobsResponse.jobs);

    const home = await api.home();
    setHomeClasses(home.classes);
    setRecentChats(home.recentChats);

    if (
      currentChatId &&
      !chatsResponse.chats.some((chat) => chat.id === currentChatId)
    ) {
      setSelectedChatId(chatsResponse.chats[0]?.id ?? null);
      return;
    }

    if (!currentChatId && chatsResponse.chats[0]) {
      setSelectedChatId(chatsResponse.chats[0].id);
    }
  }

  async function bootstrap() {
    const [{ user: currentUser }, bootstrapStatus] = await Promise.all([
      api.me(),
      api.bootstrapStatus(),
    ]);

    setAllowAdminBootstrap(bootstrapStatus.allowAdminBootstrap);
    setBootstrapMode(bootstrapStatus.bootstrapMode);
    setUser(currentUser);
    if (!currentUser) return;

    const { classes: nextClasses } = await api.listClasses();
    setClasses(nextClasses);
    const home = await api.home();
    setHomeClasses(home.classes);
    setRecentChats(home.recentChats);
    if (!selectedClassId && currentUser.role === "STUDENT" && nextClasses[0]) {
      setSelectedClassId(nextClasses[0].id);
    }
  }

  useEffect(() => {
    void bootstrap().catch((error) => setStatusMessage(error.message));
  }, []);

  useEffect(() => {
    if (!user || !selectedClassId) return;

    void loadClassWorkspaceData(selectedClassId, selectedChatId).catch((error) =>
      setStatusMessage(error.message),
    );
  }, [user, selectedClassId]);

  useEffect(() => {
    if (!user || !selectedClassId) return;
    if (activeJobCount === 0) return;

    const timer = window.setInterval(() => {
      void Promise.all([
        api.listFiles(selectedClassId),
        api.listJobs(selectedClassId),
      ])
        .then(([filesResponse, jobsResponse]) => {
          setFiles(filesResponse.files);
          setJobs(jobsResponse.jobs);
        })
        .catch(() => {
          // Keep polling quiet when the user changes classes or logs out.
        });
    }, 4000);

    return () => {
      window.clearInterval(timer);
    };
  }, [user, selectedClassId, activeJobCount]);

  useEffect(() => {
    if (!user || !selectedClassId || !selectedChat) return;
    if (activeJobCount !== 0) return;

    void api
      .listMessages(selectedChat.id)
      .then((response) => setMessages(response.messages))
      .catch(() => {
        setStatusMessage(
          "Unable to refresh the latest messages. Please reopen the thread.",
        );
      });
  }, [user, selectedClassId, selectedChat, activeJobCount]);

  useEffect(() => {
    if (!user || !selectedChat) {
      setMessages([]);
      setLiveStatus("offline");
      return;
    }

    void api
      .listMessages(selectedChat.id)
      .then((response) => setMessages(response.messages))
      .catch((error) => setStatusMessage(error.message));
  }, [user, selectedChat]);

  useEffect(() => {
    if (!selectedChat) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(
      `${protocol}//${window.location.host}/api/live/${selectedChat.id}`,
    );
    let closing = false;
    setLiveStatus("connecting");

    socket.onopen = () => {
      setLiveStatus("live");
    };

    socket.onmessage = (event) => {
      const live = JSON.parse(event.data as string) as LiveEvent;
      if (
        (live.type === "user-message" || live.type === "assistant-message") &&
        live.chatId === selectedChat.id
      ) {
        setMessages((current) => {
          if (current.some((item) => item.id === live.payload.id)) {
            return current;
          }
          return [...current, live.payload].sort((left, right) =>
            left.created_at.localeCompare(right.created_at),
          );
        });
      }
    };

    socket.onerror = () => {
      setLiveStatus("offline");
      setStatusMessage(
        "Live updates are unavailable right now. New replies may appear after a refresh.",
      );
    };

    socket.onclose = () => {
      setLiveStatus("offline");
      if (!closing) {
        setStatusMessage(
          "Live connection closed. New replies may appear after a refresh.",
        );
      }
    };

    return () => {
      closing = true;
      socket.close();
    };
  }, [selectedChat]);

  useEffect(() => {
    if (!selectedChat) return;

    const failedJob = jobs.find(
      (job) =>
        job.chat_id === selectedChat.id &&
        job.type === "assistant-reply" &&
        job.status === "failed" &&
        job.result_json?.includes("error"),
    );

    if (!failedJob) return;

    try {
      const parsed = JSON.parse(failedJob.result_json ?? "{}") as {
        error?: string;
      };
      if (parsed.error) {
        setStatusMessage(`Assistant reply failed: ${parsed.error}`);
      }
    } catch {
      setStatusMessage(
        "Assistant reply failed. Check the activity panel for more detail.",
      );
    }
  }, [jobs, selectedChat]);

  async function refreshClassData() {
    if (!selectedClassId) return;
    await loadClassWorkspaceData(selectedClassId, selectedChatId);
  }

  async function handleRegister(event: React.FormEvent) {
    event.preventDefault();
    try {
      const response = await api.register(registerForm);
      setUser(response.user);
      setRegisterForm(emptyRegister);
      setStatusMessage(
        response.bootstrap.wasFirstUser
          ? `Signed in as ${response.user.display_name}. First account bootstrapped as ADMIN.`
          : `Signed in as ${response.user.display_name}.`,
      );
      await bootstrap();
    } catch (errorValue) {
      setStatusMessage((errorValue as Error).message);
    }
  }

  async function handleLogin(event: React.FormEvent) {
    event.preventDefault();
    try {
      const response = await api.login(loginForm);
      setUser(response.user);
      setLoginForm(emptyLogin);
      setStatusMessage(`Welcome back, ${response.user.display_name}.`);
      await bootstrap();
    } catch (errorValue) {
      setStatusMessage((errorValue as Error).message);
    }
  }

  function resetClassroomSelection() {
    setSelectedClassId(null);
    setSelectedChatId(null);
    setMembers([]);
    setFiles([]);
    setJobs([]);
    setChats([]);
    setMessages([]);
    setLiveStatus("offline");
  }

  async function handleLogout() {
    await api.logout();
    setUser(null);
    setAuthView("login");
    setClasses([]);
    setHomeClasses([]);
    setRecentChats([]);
    resetClassroomSelection();
  }

  async function handleCreateClass(event: React.FormEvent) {
    event.preventDefault();
    try {
      const response = await api.createClass(classForm);
      const nextClasses = [response.class, ...classes];
      setClasses(nextClasses);
      const home = await api.home();
      setHomeClasses(home.classes);
      setRecentChats(home.recentChats);
      resetClassroomSelection();
      setClassForm({ name: "", subject: "", gradeBand: "Years 5-6" });
      setStatusMessage(`Created class "${response.class.name}".`);
    } catch (errorValue) {
      setStatusMessage((errorValue as Error).message);
    }
  }

  async function handleAddMember(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedClassId) return;
    try {
      await api.addMember(selectedClassId, memberForm);
      setMemberForm({ email: "", role: "STUDENT" });
      await refreshClassData();
      setStatusMessage("Member updated.");
    } catch (errorValue) {
      setStatusMessage((errorValue as Error).message);
    }
  }

  async function handleCreateChat(event: React.FormEvent) {
    event.preventDefault();
    try {
      const response = await api.createChat({
        classId: selectedClassId,
        title:
          chatTitle ||
          (selectedClass ? `${selectedClass.name} lab` : "Study chat"),
      });
      setChats((current) => [response.chat, ...current]);
      setSelectedChatId(response.chat.id);
      setChatTitle("");
      setStatusMessage("New classroom chat created.");
    } catch (errorValue) {
      setStatusMessage((errorValue as Error).message);
    }
  }

  function populateComposer(prompt: string) {
    setMessageDraft(prompt);
    window.requestAnimationFrame(() => {
      composerRef.current?.focus();
      composerRef.current?.setSelectionRange(prompt.length, prompt.length);
    });
  }

  async function handleStarterSelect(starter: {
    id: string;
    label: string;
    prompt: string;
  }) {
    populateComposer(starter.prompt);

    if (selectedChatId || !selectedClassId) {
      setStatusMessage(
        `Prompt ready: ${starter.label}. Review it and send when you're ready.`,
      );
      return;
    }

    try {
      setIsCreatingChat(true);
      const response = await api.createChat({
        classId: selectedClassId,
        title: starter.label,
      });
      setChats((current) => [response.chat, ...current]);
      setSelectedChatId(response.chat.id);
      setStatusMessage(
        `Created "${response.chat.title}". Review the starter prompt and send when ready.`,
      );
    } catch (errorValue) {
      setStatusMessage((errorValue as Error).message);
    } finally {
      setIsCreatingChat(false);
    }
  }

  async function handleSendMessage(event: React.FormEvent) {
    event.preventDefault();
    await submitDraft("chat");
  }

  async function submitDraft(mode: "chat" | "image") {
    const nextDraft = messageDraft.trim();
    if (!selectedChatId || !nextDraft || selectedChatPendingReply) return;
    try {
      setIsSendingMessage(true);
      const response = await api.sendMessage(selectedChatId, {
        content: nextDraft,
        mode,
      });
      setMessages((current) => {
        if (current.some((message) => message.id === response.message.id)) {
          return current;
        }

        return [...current, response.message].sort((left, right) =>
          left.created_at.localeCompare(right.created_at),
        );
      });
      setMessageDraft("");
      setStatusMessage(
        mode === "image"
          ? "Prompt sent. RoboLabX is drawing a classroom visual."
          : "Prompt sent. RoboLabX is preparing a classroom-ready reply.",
      );
      await refreshClassData();
    } catch (errorValue) {
      setStatusMessage((errorValue as Error).message);
    } finally {
      setIsSendingMessage(false);
    }
  }

  function handleApplyPrompt(prompt: string) {
    populateComposer(prompt);
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!selectedClassId) return;
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingFileName(file.name);
      await api.uploadFile(selectedClassId, file);
      await refreshClassData();
      setStatusMessage(
        `Queued ${file.name} for classroom materials. Mention it by name once it is ready.`,
      );
    } catch (errorValue) {
      setStatusMessage((errorValue as Error).message);
    } finally {
      setUploadingFileName(null);
      event.target.value = "";
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, selectedChatPendingReply]);

  if (!user) {
    return (
      <div className="auth-scene">
        <div className="auth-shell auth-shell-compact">
          <section className="auth-stack">
            <div className="auth-brand-card">
              <div className="auth-logo-box">R</div>
              <div>
                <h1>RoboLab X</h1>
                <p className="auth-brand-subtitle">Powered by COSX AI</p>
              </div>
            </div>

            {authView === "login" ? (
              <>
                <div className="auth-card auth-card-primary">
                  <div className="auth-card-header auth-card-header-compact">
                    <div>
                      <h2>Sign in</h2>
                      <p className="panel-note">
                        Enter your existing classroom workspace.
                      </p>
                    </div>
                  </div>
                  <form className="stack-form" onSubmit={handleLogin}>
                    <label>
                      <span>Username or email</span>
                      <input
                        value={loginForm.email}
                        onChange={(event) =>
                          setLoginForm((current) => ({
                            ...current,
                            email: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      <span>Password</span>
                      <input
                        type="password"
                        value={loginForm.password}
                        onChange={(event) =>
                          setLoginForm((current) => ({
                            ...current,
                            password: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <button type="submit">Sign in</button>
                  </form>
                </div>

                <div className="auth-card auth-card-secondary auth-switch-card">
                  <div className="auth-card-header auth-card-header-compact">
                    <div>
                      <h2>Need an account?</h2>
                      <p className="panel-note">
                        New teachers and students can continue to a separate registration screen.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => setAuthView("register")}
                  >
                    Continue to create account
                  </button>
                </div>
              </>
            ) : (
              <div className="auth-card auth-card-secondary">
                <div className="auth-card-header">
                  <div>
                    <h2>Create account</h2>
                    <p className="panel-note">
                      Set up a teacher or student account, then return to class.
                    </p>
                  </div>
                  <div className="auth-header-actions">
                    <span className="card-chip">
                      {allowAdminBootstrap ? "Bootstrap open" : "Join workspace"}
                    </span>
                    <button
                      type="button"
                      className="ghost-button compact-button"
                      onClick={() => setAuthView("login")}
                    >
                      Back to sign in
                    </button>
                  </div>
                </div>
                <form className="stack-form" onSubmit={handleRegister}>
                  <label>
                    <span>Email</span>
                    <input
                      value={registerForm.email}
                      onChange={(event) =>
                        setRegisterForm((current) => ({
                          ...current,
                          email: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    <span>Display name</span>
                    <input
                      value={registerForm.displayName}
                      onChange={(event) =>
                        setRegisterForm((current) => ({
                          ...current,
                          displayName: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    <span>Password</span>
                    <input
                      type="password"
                      value={registerForm.password}
                      onChange={(event) =>
                        setRegisterForm((current) => ({
                          ...current,
                          password: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    <span>Role</span>
                    <select
                      value={registerForm.role}
                      onChange={(event) =>
                        setRegisterForm((current) => ({
                          ...current,
                          role: event.target.value as User["role"],
                        }))
                      }
                    >
                      {allowAdminBootstrap && (
                        <option value="ADMIN">Admin bootstrap</option>
                      )}
                      <option value="TEACHER">Teacher</option>
                      <option value="STUDENT">Student</option>
                    </select>
                  </label>
                  <p className="panel-note auth-note">
                    {allowAdminBootstrap
                      ? bootstrapMode === "restricted"
                        ? "The first account is reserved for the configured owner email."
                        : "The first account becomes the platform admin automatically."
                      : "Admin bootstrap is closed. New users can join as teachers or students only."}
                  </p>
                  <button type="submit" className="secondary-button">
                    Create account
                  </button>
                </form>
              </div>
            )}
          </section>
        </div>

        {statusMessage && (
          <p className="status-banner auth-banner">{statusMessage}</p>
        )}
      </div>
    );
  }

  return (
    <div className="app-scene">
      <div className={`workspace-page workspace-page-${surface}`}>
        <header className="app-shell-header">
          <div className="app-shell-brand">
            <div>
              <p className="eyebrow">Cloudflare standalone</p>
              <h1>RoboLabX</h1>
              <p className="workspace-subtitle">
                {surface === "home"
                  ? `${workspaceLabelForRole(user.role)} · Start from Home, then enter one class at a time.`
                  : `${selectedClass?.name ?? "Classroom"} · Keep the room focused on one active thread.`}
              </p>
            </div>
          </div>

          <div className="app-shell-actions">
            {surface === "classroom" && (
              <button
                className="ghost-button compact-button"
                onClick={resetClassroomSelection}
              >
                Back to Home
              </button>
            )}
            <div className="profile-card profile-card-compact">
              <div className="profile-avatar">
                {user.display_name.slice(0, 1).toUpperCase()}
              </div>
              <div>
                <strong>{user.display_name}</strong>
                <p>{user.role.toLowerCase()}</p>
              </div>
            </div>
            <button
              className="ghost-button compact-button"
              onClick={() => void handleLogout()}
            >
              Sign out
            </button>
          </div>
        </header>

        {statusMessage && <p className="status-banner">{statusMessage}</p>}

        {surface === "home" ? (
          <section className="home-surface">
            <div className="home-primary">
              <section className="home-hero-card">
                <div>
                  <span className="section-label">Home</span>
                  <h2>Choose one class and keep the next move calm.</h2>
                  <p className="workspace-subtitle">
                    Home is for overview and re-entry. Classroom tools stay out of the way until a class is active.
                  </p>
                </div>
                <div className="home-hero-metrics">
                  <div className="hero-metric">
                    <span className="metric-label">Active now</span>
                    <strong>{activeHomeClasses.length}</strong>
                  </div>
                  <div className="hero-metric">
                    <span className="metric-label">All classes</span>
                    <strong>{homeClassSummaries.length}</strong>
                  </div>
                  <div className="hero-metric">
                    <span className="metric-label">Recent chats</span>
                    <strong>{recentChats.length}</strong>
                  </div>
                </div>
              </section>

              {homeClassSummaries.length > 0 ? (
                <div className="home-class-groups">
                  {activeHomeClasses.length > 0 && (
                    <section className="classes-group home-group-card">
                      <div className="classes-group-header">
                        <div>
                          <span className="section-label">Active now</span>
                          <p className="panel-note">
                            Re-enter classes that already have conversation momentum.
                          </p>
                        </div>
                        <span className="count-chip">{activeHomeClasses.length}</span>
                      </div>
                      <div className="classes-home-grid">
                        {activeHomeClasses.map((item) => (
                          <button
                            key={item.id}
                            className="class-overview-card active-overview-card"
                            onClick={() => {
                              setSelectedClassId(item.id);
                              setSelectedChatId(null);
                            }}
                          >
                            <div className="class-overview-icon">
                              {item.name.slice(0, 1).toUpperCase()}
                            </div>
                            <div className="class-overview-copy">
                              <h3>{item.name}</h3>
                              <p>{item.subject || "No topic yet"}</p>
                              <small>
                                {chatCountLabel(item.chat_count)} · {item.grade_band}
                              </small>
                            </div>
                          </button>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="classes-group home-group-card">
                    <div className="classes-group-header">
                      <div>
                        <span className="section-label">All classes</span>
                        <p className="panel-note">
                          Every class remains available from Home without bringing the classroom shell on-screen.
                        </p>
                      </div>
                      <span className="count-chip">{idleHomeClasses.length}</span>
                    </div>
                    <div className="classes-home-grid">
                      {idleHomeClasses.map((item) => (
                        <button
                          key={item.id}
                          className="class-overview-card"
                          onClick={() => {
                            setSelectedClassId(item.id);
                            setSelectedChatId(null);
                          }}
                        >
                          <div className="class-overview-icon">
                            {item.name.slice(0, 1).toUpperCase()}
                          </div>
                          <div className="class-overview-copy">
                            <h3>{item.name}</h3>
                            <p>{item.subject || "No topic yet"}</p>
                            <small>
                              {item.grade_band} · {chatCountLabel(item.chat_count)}
                            </small>
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>
                </div>
              ) : (
                <div className="empty-state workspace-empty">
                  {canManageControls
                    ? "Create a class to open the first classroom."
                    : "Your classes will appear here as soon as a teacher or admin adds you to one."}
                </div>
              )}
            </div>

            <aside className="home-utility-rail">
              <section className="home-utility-card">
                <div className="classes-group-header">
                  <div>
                    <span className="section-label">Recent activity</span>
                    <p className="panel-note">Useful for re-entry, not the main focus.</p>
                  </div>
                  <span className="count-chip">{recentChats.length}</span>
                </div>
                <div className="thread-list">
                  {recentChats.length > 0 ? (
                    recentChats.map((chat) => (
                      <button
                        key={chat.id}
                        className="thread-card"
                        onClick={() => {
                          if (chat.class_id) {
                            setSelectedClassId(chat.class_id);
                            setSelectedChatId(chat.id);
                          }
                        }}
                      >
                        <div className="thread-card-top">
                          <strong>{chat.title}</strong>
                          <span className="thread-badge subtle-badge">Chat</span>
                        </div>
                        <span className="thread-preview">
                          {chat.class_name ?? "General workspace"}
                        </span>
                        <small>{formatDateTimeLabel(chat.updated_at)}</small>
                      </button>
                    ))
                  ) : (
                    <div className="mini-card">
                      <strong>No recent chats yet.</strong>
                      <span>Conversations will appear here after classes become active.</span>
                    </div>
                  )}
                </div>
              </section>

              {canManageControls && (
                <section className="home-utility-card">
                  <div className="classes-group-header">
                    <div>
                      <span className="section-label">Create class</span>
                      <p className="panel-note">Keep setup secondary to class entry.</p>
                    </div>
                  </div>
                  <form className="stack-form" onSubmit={handleCreateClass}>
                    <label>
                      <span>Class name</span>
                      <input
                        placeholder="Example: Solar System Lab"
                        value={classForm.name}
                        onChange={(event) =>
                          setClassForm((current) => ({
                            ...current,
                            name: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <div className="sidebar-form-grid">
                      <label>
                        <span>Subject</span>
                        <input
                          placeholder="Science"
                          value={classForm.subject}
                          onChange={(event) =>
                            setClassForm((current) => ({
                              ...current,
                              subject: event.target.value,
                            }))
                          }
                        />
                      </label>
                      <label>
                        <span>Grade band</span>
                        <input
                          placeholder="Years 5-6"
                          value={classForm.gradeBand}
                          onChange={(event) =>
                            setClassForm((current) => ({
                              ...current,
                              gradeBand: event.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>
                    <button type="submit" className="secondary-button">
                      Create class
                    </button>
                  </form>
                </section>
              )}
            </aside>
          </section>
        ) : selectedClass ? (
          <section className="classroom-surface">
            <section className="classroom-header-card">
              <div>
                <span className="section-label">Classroom</span>
                <h2>{selectedClass.name}</h2>
                <p className="workspace-subtitle">
                  {selectedChat
                    ? `${selectedChat.title} is active. Read the room, then send the next move.`
                    : "Choose one thread or create a new one. Roster, materials, and activity stay demoted until you need them."}
                </p>
              </div>
              <div className="classroom-header-meta">
                <span className="pill">{selectedClass.subject}</span>
                <span className="pill">{selectedClass.grade_band}</span>
                <span className={`pill ${liveStatus === "live" ? "accent-pill" : ""}`}>
                  {selectedChat ? liveStatus : "Choose chat"}
                </span>
              </div>
              <div className="classroom-summary-grid">
                <article className="summary-card">
                  <span className="metric-label">Threads</span>
                  <strong>{chats.length}</strong>
                  <p>{chats.length === 0 ? "Create the first thread." : "Keep one room active at a time."}</p>
                </article>
                <article className="summary-card">
                  <span className="metric-label">Materials</span>
                  <strong>{readyFileCount}</strong>
                  <p>
                    {readyFileCount > 0
                      ? `${pendingFileCount} still processing.`
                      : canManageControls
                        ? "Upload only when class context needs it."
                        : "Shared materials appear here once staff uploads them."}
                  </p>
                </article>
                <article className="summary-card">
                  <span className="metric-label">Roster</span>
                  <strong>{members.length}</strong>
                  <p>
                    {canManageControls
                      ? "Add or update members from the demoted tools drawer."
                      : "Review class members from the demoted tools drawer."}
                  </p>
                </article>
              </div>
            </section>

            <div className="classroom-layout">
              <aside className="thread-rail classroom-thread-rail">
                <div className="rail-header">
                  <div>
                    <span className="section-label">Threads</span>
                    <p>Switch the active classroom conversation.</p>
                  </div>
                  <span className="count-chip">{chats.length}</span>
                </div>

                <details className="thread-compose-panel">
                  <summary>Start a new thread</summary>
                  <form className="stack-form thread-create-form" onSubmit={handleCreateChat}>
                    <input
                      placeholder="New chat title"
                      value={chatTitle}
                      onChange={(event) => setChatTitle(event.target.value)}
                    />
                    <button type="submit" className="secondary-button">
                      Create chat
                    </button>
                  </form>
                </details>

                <div className="thread-list">
                  {chats.length > 0 ? (
                    chats.map((chat) => (
                      <button
                        key={chat.id}
                        className={`thread-card ${chat.id === selectedChatId ? "active" : ""}`}
                        onClick={() => setSelectedChatId(chat.id)}
                      >
                        <div className="thread-card-top">
                          <strong>{chat.title}</strong>
                          <span className="thread-badge">
                            {chat.id === selectedChatId ? "Live" : "Open"}
                          </span>
                        </div>
                        <span className="thread-preview">
                          {chat.id === selectedChatId
                            ? buildChatPreview(chat.id, messages)
                            : "Open this thread to review the latest classroom exchange."}
                        </span>
                        <small>{formatDateTimeLabel(chat.updated_at)}</small>
                      </button>
                    ))
                  ) : (
                    <div className="mini-card">
                      <strong>No classroom chats yet.</strong>
                      <span>Open one thread, then send a single clear starter.</span>
                    </div>
                  )}
                </div>
              </aside>

              <div className="thread-stage classroom-thread-stage">
                <div className="thread-stage-header">
                  <div className="thread-stage-meta">
                    <span className="section-label">Conversation</span>
                    <h3>{selectedChat ? selectedChat.title : "Choose or create a thread"}</h3>
                    <p className="thread-stage-subtitle">
                      {selectedClass.name} · {selectedClass.subject}
                    </p>
                  </div>
                  <div className="topbar-badges">
                    <span className="count-chip">
                      {messages.length} {messages.length === 1 ? "message" : "messages"}
                    </span>
                    {selectedChat && (
                      <span
                        className={`thread-badge ${liveStatus === "live" ? "" : "subtle-badge"}`}
                      >
                        {liveStatus}
                      </span>
                    )}
                  </div>
                </div>

                {selectedChat && messages.length === 0 && (
                  <section className="launchpad-card quiet-launchpad-card">
                    <div className="launchpad-header">
                      <div>
                        <span className="section-label">Starter prompts</span>
                        <p className="panel-note">Use one strong opening move.</p>
                      </div>
                      <span className="count-chip">{starterPrompts.length}</span>
                    </div>
                    <div className="prompt-chip-row">
                      {starterPrompts.map((prompt) => (
                        <button
                          key={prompt.id}
                          type="button"
                          className="secondary-button prompt-chip"
                          onClick={() => handleApplyPrompt(prompt.prompt)}
                        >
                          {prompt.label}
                        </button>
                      ))}
                    </div>
                    <p className="panel-note">
                      {readyReferenceFiles.length > 0
                        ? "Mention one material by file name when it strengthens the prompt."
                        : "Keep the first prompt grounded in learner level and output."}
                    </p>
                  </section>
                )}

                {pendingAssistantReplyCount > 0 && (
                  <section className="assistant-wait-card compact-wait-card">
                    <div>
                      <span className="section-label">Reply in progress</span>
                      <strong>
                        RoboLabX is drafting {pendingAssistantReplyCount} classroom response
                        {pendingAssistantReplyCount === 1 ? "" : "s"}.
                      </strong>
                    </div>
                    <p className="panel-note">
                      Keep the room moving by preparing the next follow-up while the active reply finishes.
                    </p>
                  </section>
                )}

                <div className="messages classroom-messages">
                  {selectedChat ? (
                    messages.length > 0 ? (
                      <>
                        {messages.map((message) => (
                          <article
                            key={message.id}
                            className={`message ${message.role === "ASSISTANT" ? "assistant" : "user"}`}
                          >
                            <header>
                              <div className="message-heading">
                                <strong>
                                  {message.author_display_name ??
                                    (message.role === "ASSISTANT"
                                      ? "RoboLabX"
                                      : "Class member")}
                                </strong>
                                {isFallbackAssistantMessage(message) && (
                                  <span className="thread-badge subtle-badge">
                                    Fallback
                                  </span>
                                )}
                              </div>
                              <span>{formatTimeLabel(message.created_at)}</span>
                            </header>
                            {message.image_url && (
                              <img
                                className="message-generated-image"
                                src={message.image_url}
                                alt={message.content || "Generated classroom visual"}
                              />
                            )}
                            {message.content && <p>{message.content}</p>}
                          </article>
                        ))}
                        {selectedChatPendingReply && (
                          <article className="message assistant pending-message">
                            <header>
                              <div className="message-heading">
                                <strong>RoboLabX</strong>
                                <span className="thread-badge subtle-badge">
                                  Drafting
                                </span>
                              </div>
                            </header>
                            <p>
                              <span className="typing-dots" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                              </span>
                              Preparing the next classroom-ready reply.
                            </p>
                          </article>
                        )}
                        <div ref={messagesEndRef} />
                      </>
                    ) : (
                      <div className="empty-state thread-empty-state">
                        <strong>Start this thread with one clear first move.</strong>
                        <p>
                          Pick one starter prompt and send it. The conversation should dominate once the room begins.
                        </p>
                        <div className="empty-state-actions">
                          {starterPrompts.slice(0, 4).map((starter) => (
                            <button
                              key={starter.id}
                              type="button"
                              className="secondary-button"
                              onClick={() => handleApplyPrompt(starter.prompt)}
                            >
                              {starter.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="empty-state thread-empty-state">
                      <strong>Choose a thread with a concrete learning goal.</strong>
                      <p>
                        Start one chat, then send one strong starter. Secondary tools stay collapsed until you need them.
                      </p>
                      <div className="empty-state-actions">
                        {starterPrompts.map((starter) => (
                          <button
                            key={starter.id}
                            type="button"
                            className="secondary-button"
                            onClick={() => void handleStarterSelect(starter)}
                            disabled={isCreatingChat}
                          >
                            {starter.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <form className="composer classroom-composer" onSubmit={handleSendMessage}>
                  <textarea
                    ref={composerRef}
                    placeholder={
                      selectedChatPendingReply
                        ? "RoboLabX is drafting a reply. Refine the next prompt while you wait."
                        : "Ask for a warm-up, explanation, rubric, or classroom exercise."
                    }
                    value={messageDraft}
                    onChange={(event) => setMessageDraft(event.target.value)}
                    disabled={isSendingMessage}
                  />
                    <div className="composer-actions">
                      <div className="composer-meta">
                        <p className="composer-hint">
                          {selectedChatPendingReply
                            ? "A reply is already in flight. Keep drafting, then send the next prompt after it lands."
                            : "Name the learner level, expected output, and any material by file name. Use Draw when the next turn should be a visual."}
                        </p>
                      <small className="composer-counter">
                        {selectedChatPendingReply
                          ? "Reply in progress"
                          : messageDraft.trim().length > 0
                            ? `${messageDraft.trim().length} characters ready to send`
                            : "No prompt drafted yet"}
                      </small>
                    </div>
                      <div className="composer-button-row">
                        <button
                          type="button"
                          className="secondary-button"
                          disabled={
                            !selectedChat ||
                            !messageDraft.trim() ||
                            isSendingMessage ||
                            selectedChatPendingReply
                          }
                          onClick={() => void submitDraft("image")}
                        >
                          {selectedChatPendingReply ? "Reply in progress" : "Draw"}
                        </button>
                        <button
                          type="submit"
                          disabled={
                            !selectedChat ||
                            !messageDraft.trim() ||
                            isSendingMessage ||
                            selectedChatPendingReply
                          }
                        >
                          {isSendingMessage
                            ? "Sending…"
                            : selectedChatPendingReply
                              ? "Reply in progress"
                              : "Send"}
                        </button>
                      </div>
                    </div>
                  </form>

                <details className="classroom-drawer">
                  <summary>
                    <div>
                      <span className="section-label">Class tools</span>
                      <strong>Roster, materials, and activity</strong>
                    </div>
                    <span className="drawer-summary">
                      {workflowCards.map((card) => `${card.title}: ${card.status}`).join(" · ")}
                    </span>
                  </summary>

                  <div className="workflow-card-grid">
                    {workflowCards.map((card) => (
                      <article key={card.id} className="workflow-card">
                        <span className="metric-label">{card.label}</span>
                        <strong>{card.title}</strong>
                        <span className="thread-badge subtle-badge">{card.status}</span>
                        <p>{card.detail}</p>
                      </article>
                    ))}
                  </div>

                  <div className="classroom-tools-grid">
                    <section className="inspector-card tool-card">
                      <div className="inspector-card-header">
                        <h4>Roster</h4>
                        <span className="count-chip">{members.length}</span>
                      </div>
                      {canManageControls ? (
                        <form className="stack-form compact-form" onSubmit={handleAddMember}>
                          <input
                            placeholder="Existing user email"
                            value={memberForm.email}
                            onChange={(event) =>
                              setMemberForm((current) => ({
                                ...current,
                                email: event.target.value,
                              }))
                            }
                          />
                          <div className="sidebar-form-grid">
                            <select
                              value={memberForm.role}
                              onChange={(event) =>
                                setMemberForm((current) => ({
                                  ...current,
                                  role: event.target.value as "TEACHER" | "STUDENT",
                                }))
                              }
                            >
                              <option value="STUDENT">Student</option>
                              <option value="TEACHER">Teacher</option>
                            </select>
                            <button type="submit" className="secondary-button">
                              Update
                            </button>
                          </div>
                        </form>
                      ) : (
                        <p className="panel-note">
                          Students can review the roster but cannot change access.
                        </p>
                      )}
                      <div className="mini-list">
                        {members.length > 0 ? (
                          members.map((member) => (
                            <div key={member.id} className="mini-card member-card">
                              <div className="mini-card-top">
                                <strong>{member.display_name}</strong>
                                <span className="thread-badge subtle-badge">
                                  {member.role}
                                </span>
                              </div>
                              <span>{member.email}</span>
                            </div>
                          ))
                        ) : (
                          <div className="mini-card">
                            <strong>No members yet.</strong>
                            <span>Add the first learner or teacher when you are ready.</span>
                          </div>
                        )}
                      </div>
                    </section>

                    <section className="inspector-card tool-card">
                      <div className="inspector-card-header">
                        <h4>Materials</h4>
                        <span className="count-chip">{files.length}</span>
                      </div>
                      <div className="inspector-summary-row">
                        <span>{readyFileCount} ready</span>
                        <span>{pendingFileCount} processing</span>
                        <span>{imageFileCount} images</span>
                      </div>
                      {canManageControls ? (
                        <label className="upload-field">
                          <span>Upload classroom material</span>
                          <small className="upload-field-note">
                            Stage images, PDFs, worksheets, or slides for shared context. Mention the material by name in the prompt.
                          </small>
                          <input
                            type="file"
                            accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.csv"
                            disabled={Boolean(uploadingFileName)}
                            onChange={(event) => void handleFileChange(event)}
                          />
                        </label>
                      ) : (
                        <p className="panel-note">
                          Materials are visible here after a teacher or admin stages them for the class.
                        </p>
                      )}
                      {uploadingFileName && (
                        <p className="panel-note">Uploading {uploadingFileName}…</p>
                      )}
                      <div className="mini-list">
                        {files.length > 0 ? (
                          files.map((file) => (
                            <div key={file.id} className="mini-card file-card">
                              <div className="mini-card-top">
                                <strong>{file.filename}</strong>
                                <div className="badge-row">
                                  <span className="thread-badge subtle-badge">
                                    {fileKindLabel(file)}
                                  </span>
                                  <span className="thread-badge subtle-badge">
                                    {file.status}
                                  </span>
                                </div>
                              </div>
                              <span>{Math.round(file.size_bytes / 1024)} KB</span>
                              <small>{formatDateTimeLabel(file.created_at)}</small>
                            </div>
                          ))
                        ) : (
                          <div className="mini-card">
                            <strong>No materials yet.</strong>
                            <span>
                              Upload a file when the next prompt needs shared classroom context.
                            </span>
                          </div>
                        )}
                      </div>
                    </section>

                    {showJobsPanel && (
                      <section className="inspector-card tool-card">
                        <div className="inspector-card-header">
                          <h4>Activity</h4>
                          <span className="count-chip">{jobs.length}</span>
                        </div>
                        <div className="mini-list">
                          {jobs.map((job) => (
                            <div key={job.id} className="mini-card job-card">
                              <div className="mini-card-top">
                                <strong>{job.type}</strong>
                                <span className="thread-badge subtle-badge">
                                  {job.status}
                                </span>
                              </div>
                              <span>{job.status}</span>
                              {job.status === "failed" && job.result_json && (
                                <small className="job-error">
                                  {(() => {
                                    try {
                                      const parsed = JSON.parse(job.result_json) as {
                                        error?: string;
                                      };
                                      return parsed.error ?? "Unknown job failure";
                                    } catch {
                                      return job.result_json;
                                    }
                                  })()}
                                </small>
                              )}
                              <small>{formatDateTimeLabel(job.updated_at)}</small>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}
                  </div>
                </details>
              </div>
            </div>
          </section>
        ) : (
          <div className="empty-state workspace-empty">
            The selected classroom is no longer available. Return Home and choose another class.
          </div>
        )}
      </div>
    </div>
  );
}
